import { FormGroupHeader, UserAvatar } from "@/components/shared";
import { Tender, User } from "@/graphql/__generated__/graphql";
import { EditTenderContext } from "@/layouts/EditTenderLayout";
import { useTranslation } from "react-i18next";
import { useOutletContext, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRef, useState } from "react";
import { ActionButtonConfig } from "@/components/shared/FormGroupHeader";
import DetailsFormComponent from "./DetailsForm/DetailsFormComponent";
import DetailsViewComponent from "./DetailsView/DetailsViewComponent";
import { TenderWonLostResultComponent } from "../Components";
import UpdateChanceOwnerDialog from "./Components/UpdateChanceOwnerDialog";
import OperationalOrganizations from "./OperationalOrganizations/OperationalOrganizations";
import ArchivedAlertComponent from "./Components/ArchivedAlertComponent";
import { useTenderPermissionsData } from "@/hooks/cache";

/**
 * Tender details component, handles the action buttons, the view and edit mode
 * @returns The rendered tender details component
 */
function TenderDetailsComponent() {
  // Hooks
  const { tenderByIdData, isTenderOwner } = useOutletContext<EditTenderContext>();
  const { t } = useTranslation(["translation", "common"]);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const formRef = useRef<{ handleSave: () => void }>(null);
  const { tenderID } = useParams<{ tenderID: string }>();
  const { canEditDetails } = useTenderPermissionsData(Number(tenderID));

  const tenderData = tenderByIdData?.fetchTenderById;
  const { description } = tenderData || {};

  const owner = description?.owner;

  return (
    <div>
      <FormGroupHeader
        title={t("edittender.details.title")}
        className="pr-4 pl-2"
      />
      <div className="flex flex-col gap-4 pb-[300px] pr-4 pl-2">
        <ArchivedAlertComponent isTenderOwner={isTenderOwner} tenderData={tenderData as Tender}/>

        <TenderWonLostResultComponent tenderState={tenderData?.state} reasonForLoss={tenderData?.reasonForLoss} />

        {/* Chance Owner details card */}
        <Card>
          <CardHeader className="pt-4 pb-2 select-none">
            <CardTitle className="text-lg">{t("edittender.details.chanceowner")}</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between">
            <div className="flex gap-4">
              <UserAvatar user={owner as User} className="w-12 h-12" />
              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium">{owner?.firstname} {owner?.lastname}</p>
                <p className="text-sm text-slate-500">{owner?.organization?.name}</p>
              </div>
            </div>
            <UpdateChanceOwnerDialog currentOwner={owner as User} />
          </CardContent>
        </Card>
        <OperationalOrganizations />

        {/* Details Form Section */}
        <div>
          <FormGroupHeader
            title={t("edittender.details.details")}
            actionButtons={canEditDetails ? [
              {
                text: t("common:buttons.edit"),
                variant: "outline",
                actionFn: () => setIsEditing(true),
                show: !isEditing
              },
              {
                text: t("common:buttons.save"),
                variant: "default",
                actionFn: () => {
                  formRef.current?.handleSave();
                },
                show: isEditing,
                loading: isSaving
              },
              {
                text: t("common:buttons.cancel"),
                variant: "outline",
                actionFn: () => { setIsEditing(false); },
                show: isEditing,
                disabled: isSaving
              }
            ].filter(button => button.show) as ActionButtonConfig[] : []}
          />
          {isEditing ? (
            <DetailsFormComponent
              tenderData={tenderData as Partial<Tender>}
              ref={formRef as React.RefObject<{ handleSave: () => void }>}
              setIsEditing={setIsEditing}
              setIsSaving={setIsSaving}
            />
          ) : (
            <DetailsViewComponent tenderData={tenderData as Tender} />
          )}
        </div>
      </div>
    </div>
  );
}

export default TenderDetailsComponent;
